import * as React from "react";
import { cn } from "@/lib/utils";
import { useIsTouch } from "@/hooks/use-mobile";
import { useMemo } from "react";

type AugmentedInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
  hideSpinner?: boolean;
  inputClassName?: string;
};

const Input = React.forwardRef<HTMLInputElement, AugmentedInputProps>(
  (
    {
      className,
      type,
      prefix,
      postfix,
      hideSpinner = true,
      inputClassName,
      ...props
    },
    ref,
  ) => {
    const isTouch = useIsTouch();
    const [typeToUse, inputMode] = useMemo(() => {
      if (isTouch) {
        if (type === "number") {
          return ["search", "decimal"] as const;
        }
        if (type === "text") {
          return ["search", "text"] as const;
        }
      }
      return [type, undefined] as const;
    }, [type, isTouch]);
    return (
      <div className={cn("relative h-8 flex items-center", className)}>
        {prefix && (
          <span
            className={cn(
              "absolute left-0 flex items-center justify-center h-full px-2 text-sm text-muted-foreground bg-olive-2 border border-r-0 border-olive-7 select-none",
              "peer",
              "peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
            )}
          >
            {prefix}
          </span>
        )}
        <input
          type={typeToUse}
          inputMode={inputMode}
          className={cn(
            "flex h-8 w-full border border-olive-7 bg-olive-1 px-2 py-1 text-base ring-offset-background placeholder:text-muted-foreground hover:border-olive-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            "peer",
            hideSpinner &&
              "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-moz-outer-spin-button]:appearance-none [&::-moz-inner-spin-button]:appearance-none",
            inputClassName,
          )}
          ref={ref}
          {...props}
          style={
            isTouch
              ? {
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                }
              : undefined
          }
          // Prevent autocomplete from password managers
          autoComplete="off"
          aria-autocomplete="none"
          data-form-type="other"
          data-1p-ignore
          data-lpignore="true"
          data-protonpass-ignore="true"
          onKeyDown={(e) => {
            if (["ArrowUp", "ArrowDown"].includes(e.key)) {
              e.preventDefault();
            }
            if (props.onKeyDown) {
              props.onKeyDown(e);
            }
          }}
        />
        {postfix && (
          <span
            className={cn(
              "absolute right-0 flex items-center justify-center h-full px-2 text-sm text-muted-foreground bg-olive-2 border border-l-0 border-olive-7 select-none",
              "peer",
              "peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
            )}
          >
            {postfix}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
